/**
 * 埋点实例
 */

import { saveInteraction } from '@/jmHttp/api/common';
import { TRACK_MODULE_TYPE, TRACK_EVENT_TYPE } from './trackModuleType';
import { isCooperationPage } from '@/utils/api';
import router from '@/router';

const { CLICK, SEARCH, CREATE } = TRACK_EVENT_TYPE;

const getValueByPath = (path, obj = TRACK_MODULE_TYPE) => {
  const keys = path.split('.'); // 将路径字符串分割成键数组
  let current = obj;
  let result = '';

  // 遍历键数组来逐层深入到对象中
  for (const key of keys) {
    if (current[key] === undefined) {
      // 如果某个键不存在，则返回undefined
      return undefined;
    }
    current = current[key];
    if (current.value !== undefined) {
      // 如果当前级别有value，则将其添加到结果字符串中
      result += (result === '' ? '' : '-') + current.value;
    }
  }

  return result; // 返回构建的字符串
};

const getCurrentPath = () => {
  const { path } = router.currentRoute._value;
  return path;
};

const setupTracking = {
  // 埋点方法
  track: async (sourceTypeStr, type, params = {}) => {
    try {
      const { sourceId, sourceUrl } = params;
      let content = params.content;

      const sourceType = getValueByPath(sourceTypeStr);

      // 判断是否是合作页面 在content 中加入标识
      const isCooperation = isCooperationPage();
      if (isCooperation) {
        if (content) {
          content = JSON.parse(content);
          content = JSON.stringify({
            ...content,
            isCooperation
          });
        } else {
          content = JSON.stringify({
            isCooperation
          });
        }
      }

      params = {
        sourceType: sourceType || 'default_type',
        type,
        sourceId: sourceId || null,
        sourceUrl: sourceUrl || getCurrentPath(),
        content: content || null
      };

      return await saveInteraction(params);
    } catch (error) {
      console.error('埋点失败', error);
    }
  },

  // 公共埋点 例如筛选条件点击 共同模块埋点
  commonTrack: {
    // 筛选组件条件 筛选项点击
    clickFilterItem: async (type, params) => {
      return await setupTracking.track(`${type}.child.FILTER`, CLICK, params);
    },
    // 筛选组件 关键词搜索
    searchSummary: async (type, params) => {
      return await setupTracking.track(`${type}.child.SEARCH`, SEARCH, params);
    },
    // 筛选组件 机构远程搜索
    searchOrg: async (type, params) => {
      return await setupTracking.track(`${type}.child.SEARCH_ORG`, SEARCH, params);
    },
    // 筛选组件 创建组合
    createCombination: async (type, params) => {
      return await setupTracking.track(`${type}.child.CREATE_COMBINATION`, CREATE, params);
    },
    // 筛选组件 点击已保存组合
    clickSavedCombination: async (type, params) => {
      return await setupTracking.track(`${type}.child.CLICK_SAVED_COMBINATION`, CLICK, params);
    },
    // 筛选组件 删除已选的筛选条件（单个/清空）
    deleteFilter: async (type, params) => {
      return await setupTracking.track(`${type}.child.DELETE_FILTER`, CLICK, params);
    },
    // 日期筛选组件点击（日程）
    clickDateFilter: async (type, params) => {
      return await setupTracking.track(`${type}.child.DATE_FILTER`, CLICK, params);
    }
  },

  // 纪要模块埋点 sourceType = 1
  summaryTrack: {
    // 点击纪要菜单
    clickSummaryMenu: async (params) => {
      return await setupTracking.track('SUMMARY', CLICK, params);
    },
    // 点击纪要列表标题
    clickSummaryTitle: async (params) => {
      return await setupTracking.track('SUMMARY.child.SUMMARY_TITLE', CLICK, params);
    },
    // 点击纪要列表 brm帮你看 按钮
    clickBrmAiBtn: async (params) => {
      return await setupTracking.track('SUMMARY.child.BRM_AI_BTN', CLICK, params);
    },
    // 点击纪要列表 个股标签
    clickStockTag: async (params) => {
      return await setupTracking.track('SUMMARY.child.CLICK_STOCK_TAG', CLICK, params);
    },
    // 点击纪要列表 热门纪要标题
    clickHotSummaryTitle: async (params) => {
      return await setupTracking.track('SUMMARY.child.HOT_SUMMARY_TITLE', CLICK, params);
    },
    // 推荐关注-用户点击关注数据量
    clickFollowData: async (params) => {
      return await setupTracking.track('SUMMARY.child.CLICK_FOLLOW', CLICK, params);
    }
  },
  // 日程模块埋点 sourceType = 3
  scheduleTrack: {
    // 点击日程tab菜单 我的 我司 市场
    clickScheduleTab: async (type, params) => {
      return await setupTracking.track(type, CLICK, params);
    },
    // 点击日程菜单
    clickScheduleMenu: async (params) => {
      return await setupTracking.track('SCHEDULE', CLICK, params);
    },
    // 日程列表 纪要
    clickSummaryTitle: async (params) => {
      return await setupTracking.track('SCHEDULE.child.MY.child.CLICK_SUMMARY', CLICK, params);
    },
    // 日程列表 标题
    clickScheduleTitle: async (params) => {
      return await setupTracking.track('SCHEDULE.child.MY.child.ROADSHOW_TITLE', CLICK, params);
    },
    // 我的日程 新建沟通点击
    newCommunication: async (params) => {
      return await setupTracking.track('SCHEDULE.child.MY.child.NEW_COMMUNICATION', CLICK, params);
    },
    // 我的日程 日视图点击 周视图点击 月视图点击
    clickScheduleView: async (type, params) => {
      return await setupTracking.track(`SCHEDULE.child.MY.child.${type}`, CLICK, params);
    },
    // 我的日程 申请回放按钮点击
    clickApplyPlayback: async (params) => {
      return await setupTracking.track('SCHEDULE.child.MY.child.APPLY_PLAYBACK', CLICK, params);
    },
    // 我的日程 路演会议看详情
    clickRoadshowDetail: async (type, params) => {
      return await setupTracking.track(`SCHEDULE.child.MY.child.${type}`, CLICK, params);
    },
    // 我的日程 预约
    clickAppointment: async (type, params) => {
      return await setupTracking.track(`${type}.child.APPOINTMENT`, CLICK, params);
    },
    // 我司 市场 点击路演 跳转详情
    clickRoadshowToDetailPage: async (type, params) => {
      return await setupTracking.track(`${type}.child.ROADSHOW_TITLE`, CLICK, params);
    },
    // 我司 市场 点击收藏
    clickCollect: async (type, params) => {
      return await setupTracking.track(`${type}.child.CLICK_COLLECT`, CLICK, params);
    },
    // 点击更多icon
    clickMoreIcon: async (type, params) => {
      return await setupTracking.track(`${type}.child.CLICK_MORE_ICON`, CLICK, params);
    },
    // 关键词搜索
    keywordSearch: async (type, params) => {
      return await setupTracking.track(`${type}.child.SEARCH`, SEARCH, params);
    },
    // 统计用户在“日/周/月”模式下点击打开“日程详情”卡片的数据
    clickShowDetailCard: async (params) => {
      return await setupTracking.track('SCHEDULE.child.MY.child.SHOW_DETAIL_CARD', CLICK, params);
    },
    // 月视图右侧抽屉点击预约
    clickMonthViewAppointment: async (params) => {
      return await setupTracking.track('SCHEDULE.child.MY.child.APPOINTMENT', CLICK, params);
    },
    // 详情卡片点击收藏
    clickDetailCardCollect: async (params) => {
      return await setupTracking.track('SCHEDULE.child.MY.child.DETAIL_CARD_COLLECT', CLICK, params);
    },
    // 详情卡片标记重要
    markImportant: async (type, params) => {
      return await setupTracking.track('SCHEDULE.child.MY.child.DETAIL_CARD_IMPORTANT', CLICK, params);
    },
    // 点击顶部日期筛选组件
    clickDateFilter: async (type, params) => {
      return await setupTracking.track(`${type}.child.DATE_COMPONENT`, CLICK, params);
    }
  },
  // 会议模块埋点 sourceType = 4
  meetingTrack: {
    // 点击会议菜单
    clickMeetingMenu: async (params) => {
      return await setupTracking.track('MEETING', CLICK, params);
    },
    // 点击历史会议tab
    clickHistoryMeetingTab: async (params) => {
      return await setupTracking.track('MEETING.child.HISTORY_MEETING', CLICK, params);
    }
  },
  // 市场
  marketTrack: {
    // 点击市场tab
    clickMarketMenu: async (params) => {
      return await setupTracking.track('MARKET', CLICK, params);
    },
    // 统计路演快捷入口点击量
    clickRoadshowShortcut: async (params) => {
      return await setupTracking.track('MARKET.child.CLICK_ROADSHOW', CLICK, params);
    },
    // 统计纪要快捷入口点击量
    clickSummaryShortcut: async (params) => {
      return await setupTracking.track('MARKET.child.CLICK_SUMMARY', CLICK, params);
    },
    // 统计调研快捷入口点击量
    clickResearchShortcut: async (params) => {
      return await setupTracking.track('MARKET.child.CLICK_RESEARCH', CLICK, params);
    },
    // 统计研报快捷入口点击量
    clickReportShortcut: async (params) => {
      return await setupTracking.track('MARKET.child.CLICK_RESEARCH_REPORT', CLICK, params);
    },
    // 统计问答快捷入口点击量
    clickQuestionShortcut: async (params) => {
      return await setupTracking.track('MARKET.child.CLICK_QUESTION_ANSWER', CLICK, params);
    },
    // 推荐关注-用户点击关注数据量
    clickFollowData: async (params) => {
      return await setupTracking.track('MARKET.child.CLICK_FOLLOW', CLICK, params);
    },
    // 推荐关注-统计用户在我的关注页面访问上市公司主页的数据量
    clickCompanyPage: async (params) => {
      return await setupTracking.track('MARKET.child.CLICK_COMPANY_HOME', CLICK, params);
    },
    // 内容展示-统计用户在推荐/我的关注菜单，点击查看“路演”详情
    clickRoadshowDetail: async (params) => {
      return await setupTracking.track('MARKET.child.CLICK_ROADSHOW_DETAIL', CLICK, params);
    },
    // 内容展示-统计用户在推荐/我的关注单，点击查看“纪要”详情
    clickSummaryDetail: async (params) => {
      return await setupTracking.track('MARKET.child.CLICK_SUMMARY_DETAIL', CLICK, params);
    },
    // 内容展示-统计用户在推荐/我的关注菜单，点击查看“调研”详情
    clickResearchDetail: async (params) => {
      return await setupTracking.track('MARKET.child.CLICK_RESEARCH_DETAIL', CLICK, params);
    },
    // 内容展示-统计用户在推荐/我的关注菜单，点击查看“研报”详情
    clickReportDetail: async (params) => {
      return await setupTracking.track('MARKET.child.CLICK_RESEARCH_REPORT_DETAIL', CLICK, params);
    },
    // 内容展示-统计用户在推荐/我的关注菜单，点击查看“问答”详情
    clickQuestionDetail: async (params) => {
      return await setupTracking.track('MARKET.child.CLICK_QUESTION_ANSWER_DETAIL', CLICK, params);
    },
    // 我的关注-统计用户在我的关注页面，点击查看“上市公司”主页数据
    clickCompanyHome: async (params) => {
      return await setupTracking.track('MARKET.child.CLICK_MY_COMPANY_HOME', CLICK, params);
    },
    // 市场风向-统计用户点击“筛选”按钮及查看行业的数据
    clickFilter: async (params) => {
      return await setupTracking.track('MARKET.child.CLICK_FILTER', CLICK, params);
    },
    // 纪要 tab下的个股关注按钮点击
    clickSummaryFollow: async (params) => {
      return await setupTracking.track('MARKET.child.SUMMARY.child.SUMMARY_FOLLOW', CLICK, params);
    },
    // 问答 tab下的个股关注按钮点击
    clickQuestionFollow: async (params) => {
      return await setupTracking.track('MARKET.child.QUESTION.child.QUESTION_FOLLOW', CLICK, params);
    },
    // 路演 tab下的个股关注按钮点击
    clickRoadshowFollow: async (params) => {
      return await setupTracking.track('MARKET.child.ROADSHOW.child.ROADSHOW_FOLLOW', CLICK, params);
    },
    // 研报 tab下的个股关注按钮点击
    clickReportFollow: async (params) => {
      return await setupTracking.track('MARKET.child.RESEARCH_REPORT.child.RESEARCH_REPORT_FOLLOW', CLICK, params);
    }
  },
  // 全局搜索
  globalSearchTrack: {
    // 综合 纪要 个股点击关注
    clickSummaryStockFollow: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.INTEGRATED.child.SUMMARY_FOLLOW', CLICK, params);
    },
    // 综合 问答 个股点击关注
    clickQuestionStockFollow: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.INTEGRATED.child.QUESTION_FOLLOW', CLICK, params);
    },
    // 综合 研报 个股点击关注
    clickReportStockFollow: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.INTEGRATED.child.RESEARCH_REPORT_FOLLOW', CLICK, params);
    },
    // 综合 上市公司 个股点击关注
    clickCompanyStockFollow: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.INTEGRATED.child.COMPANY_FOLLOW', CLICK, params);
    },
    // 纪要模块下 个股点击关注
    clickSummaryModuleStockFollow: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.SUMMARY.child.SUMMARY_FOLLOW', CLICK, params);
    },
    // 问答模块下 个股点击关注
    clickQuestionModuleStockFollow: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.QUESTION.child.QUESTION_FOLLOW', CLICK, params);
    },
    // 上市公司模块下 个股点击关注
    clickCompanyModuleStockFollow: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.COMPANY.child.COMPANY_FOLLOW', CLICK, params);
    },
    // 研报模块下 个股点击关注
    clickReportModuleStockFollow: async (params) => {
      return await setupTracking.track(
        'GLOBAL_SEARCH.child.RESEARCH_REPORT.child.RESEARCH_REPORT_FOLLOW',
        CLICK,
        params
      );
    },
    // 综合下点击路演
    clickIntegratedRoadshow: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.INTEGRATED.child.ROADSHOW', CLICK, params);
    },
    // 综合下点击纪要
    clickIntegratedSummary: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.INTEGRATED.child.SUMMARY', CLICK, params);
    },
    // 综合下点击纪要个股
    clickIntegratedSummaryStock: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.INTEGRATED.child.SUMMARY_STOCK', CLICK, params);
    },
    // 综合下点击研报
    clickIntegratedReport: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.INTEGRATED.child.RESEARCH_REPORT', CLICK, params);
    },
    // 综合下点击研报个股
    clickIntegratedReportStock: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.INTEGRATED.child.RESEARCH_REPORT_STOCK', CLICK, params);
    },
    // 综合下点击问答
    clickIntegratedQuestion: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.INTEGRATED.child.QUESTION', CLICK, params);
    },
    // 综合下点击问答个股
    clickIntegratedQuestionStock: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.INTEGRATED.child.QUESTION_STOCK', CLICK, params);
    },
    // 纪要模块下点击标题
    clickSummaryModuleTitle: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.SUMMARY.child.SUMMARY_TITLE', CLICK, params);
    },
    // 研报模块下点击标题
    clickReportModuleTitle: async (params) => {
      return await setupTracking.track(
        'GLOBAL_SEARCH.child.RESEARCH_REPORT.child.RESEARCH_REPORT_TITLE',
        CLICK,
        params
      );
    },
    // 研报 个股标签点击
    clickReportStockTag: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.RESEARCH_REPORT.child.CLICK_STOCK_TAG', CLICK, params);
    },
    // 问答模块下点击标题
    clickQuestionModuleTitle: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.QUESTION.child.QUESTION_TITLE', CLICK, params);
    },
    // 问答模块下点击个股标签
    clickQuestionModuleStockTag: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.QUESTION.child.CLICK_STOCK_TAG', CLICK, params);
    },
    // 上市公司模块下点击标题
    clickCompanyModuleTitle: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.COMPANY.child.CLICK_COMPANY', CLICK, params);
    },
    // 综合模块下点击上市公司
    clickIntegratedCompany: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.INTEGRATED.child.COMPANY', CLICK, params);
    },
    // 日程模块下点击标题
    clickScheduleModuleTitle: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.SCHEDULE.child.CLICK_SCHEDULE', CLICK, params);
    },
    // 路演模块下点击标题
    clickRoadshowModuleTitle: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.ROADSHOW.child.CLICK_ROADSHOW', CLICK, params);
    },
    // 会议标题点击
    clickMeetingModuleTitle: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.MEETING.child.CLICK_MEETING', CLICK, params);
    },
    // 综合模块下点击会议详情
    clickIntegratedMeeting: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.INTEGRATED.child.MEETING', CLICK, params);
    },
    // 智能搜索 搜索
    searchIntelligent: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.INTEGRATED.child.AI_SEARCH', SEARCH, params);
    },
    // 智能搜索 搜索再试一次
    searchAgain: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.INTEGRATED.child.AI_SEARCH_AGAIN', CLICK, params);
    },
    // 智能搜索 点击复制
    clickCopy: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.INTEGRATED.child.AI_COPY', CLICK, params);
    },
    // 智能搜索 点击点赞
    clickLike: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.INTEGRATED.child.AI_LIKE', CLICK, params);
    },
    // 智能搜索 点击点踩
    clickDislike: async (params) => {
      return await setupTracking.track('GLOBAL_SEARCH.child.INTEGRATED.child.AI_DISLIKE', CLICK, params);
    }
  },
  // 问答
  questionTrack: {
    // 推荐关注-用户点击关注数据量
    clickFollowData: async (params) => {
      return await setupTracking.track('QUESTION.child.CLICK_FOLLOW', CLICK, params);
    },
    // 问答标题点击
    clickQuestionTitle: async (params) => {
      return await setupTracking.track('QUESTION.child.QUESTION_TITLE', CLICK, params);
    }
  },
  // 个人中心
  personalCenterTrack: {
    // 我的关注 查看上市公司
    clickCompany: async (params) => {
      return await setupTracking.track('PERSONAL_CENTER.child.MY_FOLLOW.child.CLICK_COMPANY', CLICK, params);
    },
    // 我的关注 点击关注
    clickFollow: async (params) => {
      return await setupTracking.track('PERSONAL_CENTER.child.MY_FOLLOW.child.COMPANY_FOLLOW', CLICK, params);
    },
    // 我的收藏 点击路演详情
    clickCollectionRoadshow: async (params) => {
      return await setupTracking.track('PERSONAL_CENTER.child.MY_COLLECTION.child.ROADSHOW_DETAIL', CLICK, params);
    },
    // 我的收藏 点击纪要详情
    clickCollectionSummary: async (params) => {
      return await setupTracking.track('PERSONAL_CENTER.child.MY_COLLECTION.child.SUMMARY_DETAIL', CLICK, params);
    },
    // 我的收藏 点击研报详情
    clickCollectionReport: async (params) => {
      return await setupTracking.track('PERSONAL_CENTER.child.MY_COLLECTION.child.REPORT_DETAIL', CLICK, params);
    },
    // 历史记录 点击路演详情
    clickHistoryRoadshow: async (params) => {
      return await setupTracking.track('PERSONAL_CENTER.child.MY_HISTORY.child.ROADSHOW_DETAIL', CLICK, params);
    },
    // 历史记录 点击纪要详情
    clickHistorySummary: async (params) => {
      return await setupTracking.track('PERSONAL_CENTER.child.MY_HISTORY.child.SUMMARY_DETAIL', CLICK, params);
    },
    // 历史记录 点击研报详情
    clickHistoryReport: async (params) => {
      return await setupTracking.track('PERSONAL_CENTER.child.MY_HISTORY.child.REPORT_DETAIL', CLICK, params);
    }
  },
  // 全局菜单
  globalMenuTrack: {
    // 快捷操作 收藏
    clickCollect: async (params) => {
      return await setupTracking.track('GLOBAL_MENU.child.QUICK_ACTIONS.child.CLICK_COLLECT', CLICK, params);
    },
    // 快捷操作 我的收藏
    clickMyCollection: async (params) => {
      return await setupTracking.track('GLOBAL_MENU.child.QUICK_ACTIONS.child.MY_COLLECTION', CLICK, params);
    },
    // 快捷操作 历史记录
    clickMyHistory: async (params) => {
      return await setupTracking.track('GLOBAL_MENU.child.QUICK_ACTIONS.child.MY_HISTORY', CLICK, params);
    }
  },
  // 调研-策略会
  surveyTrack: {
    // 点击策略会菜单
    clickSelect: async (params) => {
      return await setupTracking.track('SURVEY.child.SELECT', CLICK, params);
    },
    // 点击调研活动菜单
    clickSurvey: async (params) => {
      return await setupTracking.track('SURVEY.child.SURVEY', CLICK, params);
    },
    // 调研活动搜索标题
    searchSurvey: async (params) => {
      return await setupTracking.track('SURVEY.child.SURVEY.child.SEARCH_TITLE', SEARCH, params);
    },
    // 策略会搜索标题
    searchSelect: async (params) => {
      return await setupTracking.track('SURVEY.child.SELECT.child.SEARCH_TITLE', SEARCH, params);
    },
    // 调研活动标题点击
    clickSurveyTitle: async (params) => {
      return await setupTracking.track('SURVEY.child.SURVEY.child.SURVEY_TITLE', CLICK, params);
    },
    // 策略会标题点击
    clickSelectTitle: async (params) => {
      return await setupTracking.track('SURVEY.child.SELECT.child.STRATEGY_TITLE', CLICK, params);
    }
  },
  // 登录
  loginTrack: {
    clickLogin: async (params) => {
      return await setupTracking.track('LOGIN.BTN', CLICK, params);
    }
  },
  // 通讯录
  addressBookTrack: {
    // 点击通讯录 内部联系人菜单
    clickInternalContacts: async (params) => {
      return await setupTracking.track('ADDRESS_BOOK.child.INTERNAL_CONTACT', CLICK, params);
    },
    // 点击通讯录 券商研究所
    clickBrokerageResearch: async (params) => {
      return await setupTracking.track('ADDRESS_BOOK.child.BROKERAGE_RE', CLICK, params);
    }
  },
  // 发现
  discoveryTrack: {
    // 市场菜单点击
    clickMarketMenu: async (params) => {
      return await setupTracking.track('DISCOVER', CLICK, params);
    }
  },
  // 企业管理
  enterpriseManagementTrack: {
    // 点击成员管理菜单
    clickMemberManagement: async (params) => {
      return await setupTracking.track('ENTERPRISE_MANAGE.child.MEMBER_MANAGE', CLICK, params);
    },
    // 点击机构设置菜单
    clickInstitutionSetting: async (params) => {
      return await setupTracking.track('ENTERPRISE_MANAGE.child.ORG_SETTING', CLICK, params);
    },
    // 点击合作权益菜单
    clickCooperationRights: async (params) => {
      return await setupTracking.track('ENTERPRISE_MANAGE.child.COOPERATION', CLICK, params);
    },
    // 点击合作券商菜单
    clickCooperationBroker: async (params) => {
      return await setupTracking.track('ENTERPRISE_MANAGE.child.COOPERATION_BROKERAGE', CLICK, params);
    },
    // 点击数据统计菜单
    clickDataStatistics: async (params) => {
      return await setupTracking.track('ENTERPRISE_MANAGE.child.DATA_STATISTICS', CLICK, params);
    }
  },
  // 自选
  selfSelectedTrack: {
    // 个股详情概览-点击一级行业名称
    clickStockOverviewIndustry: async (params) => {
      return await setupTracking.track('SELFSELECTED.child.STOCK.child.OVERVIEW_INDUSTRY', CLICK, params);
    },
    // 个股详情概览-点击关联主题名称
    clickStockOverviewTheme: async (params) => {
      return await setupTracking.track('SELFSELECTED.child.STOCK.child.OVERVIEW_THEME', CLICK, params);
    },
    // 个股详情概览-点击相对一级行业热度更多
    clickStockOverviewHotMore: async (params) => {
      return await setupTracking.track('SELFSELECTED.child.STOCK.child.OVERVIEW_HOTMORE', CLICK, params);
    },
    // 个股详情概览-研究复盘-日线，周线，月线
    clickStockOverviewResearchChartDate: async (params) => {
      return await setupTracking.track('SELFSELECTED.child.STOCK.child.RESEARCH_CHARTDATE', CLICK, params);
    },
    // 个股详情概览-研究复盘-前复权
    clickStockOverviewResearchRehabilitation: async (params) => {
      return await setupTracking.track('SELFSELECTED.child.STOCK.child.RESEARCH_REHABILITATION', CLICK, params);
    },
    // 个股详情概览-研究复盘-业绩公告、重大公告、路演、调研、深度研报
    clickStockOverviewResearchDataType: async (params) => {
      return await setupTracking.track('SELFSELECTED.child.STOCK.child.RESEARCH_DATATYPE', CLICK, params);
    },
    // 个股详情概览-研究复盘-叠加指标：归母净利润同比增速，营业收入同比增速，PE-TTM，PB，MA5, MA20、
    clickStockOverviewResearchLineType: async (params) => {
      return await setupTracking.track('SELFSELECTED.child.STOCK.child.RESEARCH_LINETYPE', CLICK, params);
    },
    // 个股详情概览-研究复盘-分钟K线
    clickStockOverviewResearchMinuteKLine: async (params) => {
      return await setupTracking.track('SELFSELECTED.child.STOCK.child.RESEARCH_MINUTE_KLINE', CLICK, params);
    },
    // 个股详情概览-研究复盘-分时线
    clickStockOverviewResearchTimeShare: async (params) => {
      return await setupTracking.track('SELFSELECTED.child.STOCK.child.RESEARCH_TIME_SHARE', CLICK, params);
    }
  }
};

export default setupTracking;
